import { Copy, Check } from "lucide-react";
// src/pages/weight-dispute/wdUi.jsx
//
// Shared presentation kit for the merchant Weight Disputes feature: money/weight/date
// formatters plus the status + charge-status pill components. Kept deliberately small and
// dependency-light (Tailwind classes only) so both the list and detail pages read the same.
//
// Money rule that matters: a NEGATIVE amount is money owed back TO the merchant — we always
// render it as an emerald "Refund", never as a scary red charge.
import React from "react";

// ---------- formatters ----------

const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

/** ₹ in Indian locale. `null`/`undefined`/non-numeric → "-". Negatives keep their sign. */
export const formatInr = (value) => {
  const num = typeof value === "number" ? value : Number(value);
  if (value === null || value === undefined || Number.isNaN(num)) return "-";
  return inrFormatter.format(num);
};

/**
 * Grams → a human "2.20 kg" string. Under 1 kg we still show kg with 2 decimals so the unit
 * never changes on the merchant. `null`/non-numeric → "-".
 */
export const formatGrams = (grams) => {
  const num = typeof grams === "number" ? grams : Number(grams);
  if (grams === null || grams === undefined || Number.isNaN(num)) return "-";
  return `${(num / 1000).toFixed(2)} kg`;
};

/** Signed grams for variance display, e.g. "+240 g" / "-120 g" / "0 g". */
export const formatSignedGrams = (grams) => {
  const num = typeof grams === "number" ? grams : Number(grams);
  if (grams === null || grams === undefined || Number.isNaN(num)) return "-";
  const sign = num > 0 ? "+" : "";
  return `${sign}${num.toLocaleString("en-IN")} g`;
};

const dateTimeFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

/** ISO → "28 Aug 2026, 04:30 PM" in en-IN. Bad/empty input → "-". */
export const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return dateTimeFormatter.format(d);
  } catch {
    return iso;
  }
};

/** Compact date only, e.g. "28 Aug 2026". */
export const formatDateShort = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

/** Human byte size, e.g. "1.4 MB". */
export const formatBytes = (bytes) => {
  const num = Number(bytes);
  if (!num || Number.isNaN(num)) return "-";
  if (num < 1024) return `${num} B`;
  const kb = num / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

/** True when the amount is a refund owed to the merchant (strictly negative). */
export const isRefund = (amount) => typeof amount === "number" && amount < 0;

/**
 * Renders a money amount with refund semantics baked in: negative → emerald + "Refund" suffix,
 * positive → the given tone (default neutral gray). Absolute value shown for refunds so the
 * merchant reads "₹120 Refund" not "-₹120".
 */
export const MoneyAmount = ({ amount, className = "", positiveClassName = "text-gray-900" }) => {
  if (typeof amount !== "number" || Number.isNaN(amount)) {
    return <span className={className}>-</span>;
  }
  if (amount < 0) {
    return (
      <span className={`text-emerald-600 ${className}`}>
        {formatInr(Math.abs(amount))} <span className="text-xs font-medium">Refund</span>
      </span>
    );
  }
  return <span className={`${positiveClassName} ${className}`}>{formatInr(amount)}</span>;
};

// ---------- status pills ----------

// Dispute lifecycle. Copy is merchant-facing (plain words, not enum names).
const DISPUTE_STATUS_META = {
  OPEN: { label: "Action needed", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  MERCHANT_ACCEPTED: {
    label: "You accepted",
    cls: "bg-slate-100 text-slate-700 border border-slate-200",
  },
  MERCHANT_DISPUTED: {
    label: "Under review",
    cls: "bg-blue-50 text-blue-700 border border-blue-200",
  },
  DISPUTE_ACCEPTED_BY_WAYUVEGA: {
    label: "Dispute upheld",
    cls: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  },
  DISPUTE_REJECTED_BY_WAYUVEGA: {
    label: "Dispute Rejected",
    cls: "bg-rose-50 text-rose-700 border border-rose-200",
  },
};

const FALLBACK_META = { label: "-", cls: "bg-gray-100 text-gray-600 border border-gray-200" };

/** Small pill for the dispute status. Unknown statuses degrade to a neutral pill. */
export const StatusPill = ({ status, className = "" }) => {
  const meta = DISPUTE_STATUS_META[status] || {
    ...FALLBACK_META,
    label: humanizeEnum(status),
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${meta.cls} ${className}`}
      title={meta.title}
    >
      {meta.label}
    </span>
  );
};

// Wallet charge state for the extra charge.
const CHARGE_STATUS_META = {
  NOT_APPLICABLE: { label: "No charge", cls: "bg-gray-100 text-gray-600 border border-gray-200" },
  PENDING: { label: "Charge pending", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  CHARGED: { label: "Charged", cls: "bg-slate-100 text-slate-700 border border-slate-200" },
  FAILED: { label: "Retrying", cls: "bg-amber-50 text-amber-700 border border-amber-200", title: "Charge attempt failed — it will be retried automatically" },
  WAIVED: { label: "No Charge", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
};

/** Small pill for wallet charge status. Hidden entirely for NOT_APPLICABLE when `hideNa`. */
export const ChargeStatusPill = ({ chargeStatus, hideNa = false, className = "" }) => {
  if (!chargeStatus) return null;
  if (hideNa && chargeStatus === "NOT_APPLICABLE") return null;
  const meta = CHARGE_STATUS_META[chargeStatus] || {
    ...FALLBACK_META,
    label: humanizeEnum(chargeStatus),
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${meta.cls} ${className}`}
    >
      {meta.label}
    </span>
  );
};

/** "SHOUTY_ENUM" → "Shouty enum" for any status we don't have bespoke copy for. */
export function humanizeEnum(value) {
  if (!value) return "-";
  return String(value)
    .toLowerCase()
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** Actor label used across timelines ("You" for merchant, "Wayuvega" for admin). */
export const actorLabel = (actorType) => {
  const t = String(actorType || "").toUpperCase();
  if (t === "MERCHANT") return "You";
  if (t === "ADMIN" || t === "WAYUVEGA") return "Wayuvega";
  if (t === "SYSTEM") return "System";
  return humanizeEnum(actorType);
};

// Options for the status <select> filter on the list page.
export const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "OPEN", label: "Action needed" },
  { value: "MERCHANT_ACCEPTED", label: "You accepted" },
  { value: "MERCHANT_DISPUTED", label: "Under review" },
  { value: "DISPUTE_ACCEPTED_BY_WAYUVEGA", label: "Dispute upheld" },
  { value: "DISPUTE_REJECTED_BY_WAYUVEGA", label: "Dispute Rejected" },
];

/**
 * Tiny inline copy button for identifiers inside clickable rows — swallows the click so the row
 * navigation never fires, and flips to a check for a beat after copying.
 */
export const CopyIconButton = ({ value, label = "Value" }) => {
  const [copied, setCopied] = React.useState(false);
  if (!value) return null;
  const handleCopy = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard?.writeText(String(value));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex-shrink-0 rounded p-0.5 text-gray-300 transition hover:bg-gray-100 hover:text-gray-600"
      title={`Copy ${label.toLowerCase()}`}
      aria-label={`Copy ${label.toLowerCase()}`}
    >
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
    </button>
  );
};
